"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const ContactForm = dynamic(() => import("../layout/ContactForm"), {
  ssr: false,
  loading: () => (
    <div className="min-h-[240px] rounded-[10px] bg-[#f5f5f5]" aria-hidden="true" />
  ),
});

type LazyContactFormProps = {
  eager?: boolean;
};

type WindowWithIdle = Window &
  typeof globalThis & {
    requestIdleCallback?: (
      callback: IdleRequestCallback,
      options?: IdleRequestOptions
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

export default function LazyContactForm({ eager = false }: LazyContactFormProps) {
  const [shouldLoad, setShouldLoad] = useState(eager);

  useEffect(() => {
    if (shouldLoad) return;

    const load = () => setShouldLoad(true);

    const onFirstIntent = () => load();

    window.addEventListener("pointerdown", onFirstIntent, { once: true });
    window.addEventListener("keydown", onFirstIntent, { once: true });
    window.addEventListener("touchstart", onFirstIntent, {
      once: true,
      passive: true,
    });

    return () => {
      window.removeEventListener("pointerdown", onFirstIntent);
      window.removeEventListener("keydown", onFirstIntent);
      window.removeEventListener("touchstart", onFirstIntent);
    };
  }, [shouldLoad]);

  useEffect(() => {
    if (!eager || shouldLoad) return;

    const load = () => setShouldLoad(true);
    const win = window as WindowWithIdle;

    let idleId: number | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

if (typeof win.requestIdleCallback === "function") {
  idleId = win.requestIdleCallback(load, { timeout: 1800 });
} else {
  timeoutId = setTimeout(load, 1200);
}

return () => {
  if (idleId !== null && typeof win.cancelIdleCallback === "function") {
    win.cancelIdleCallback(idleId);
  }

  if (timeoutId !== null) {
    clearTimeout(timeoutId);
  }
};
  }, [eager, shouldLoad]);

  if (!shouldLoad) {
    return (
      <div
        className="min-h-[240px] rounded-[10px] bg-[#f5f5f5]"
        aria-hidden="true"
      />
    );
  }

  return <ContactForm />;
}